import { useEffect } from 'react';

import classNames from 'classnames';

import signUpEmailRiv from '../../../assets/auth/sign_up_email_v3.riv';
import { Wizard } from '../../../components/Wizard/Wizard';
import { useFlag } from '../../../services/launchDarkly';
import { useStores } from '../../../stores/useStores';
import ChooseCountry from '../ChooseCountryDialog/ChooseCountry';
import { EmailVerification } from '../EmailVerification/EmailVerification';
import { ChooseCountryWizardStepRedesign } from '../redesign-components/ChooseCountryRedesign/ChooseCountryWizardStepRedesign';
import { EmailVerificationRedesign } from '../redesign-components/EmailVerificationRedesign/EmailVerificationRedesign';
import { ResponsibleSocialPlayTermsRedesign } from '../redesign-components/ResponsibleSocialPlayTermsRedesign/ResponsibleSocialPlayTermsRedesign';
import { SignUpOptionsRedesign } from '../redesign-components/SignUpOptionsRedesign/SignUpOptionsRedesign';
import { ResponsibleSocialPlay } from '../ResponsibleSocialPlayTerms/ResponsibleSocialPlayTerms';
import SignUpEmailLanding from '../SignUpEmailLanding/SignUpEmailLanding';
import { SignUpOptions } from '../SignUpOptions/SignUpOptions';
import { SignUpPrivacy } from '../SignUpPrivacy/SignUpPrivacy';
import { SignUpRegister } from '../SignUpRegister/SignUpRegister';
import { VerificationSuccess } from '../VerificationSuccess/VerificationSuccess';
import WelcomeBonusDialog from '../WelcomeBonusDialog/WelcomeBonusDialog';

import {
  SignUpWizardSteps,
  SignupWizardOrderRedesignFlow,
  DirectSignUpLandingOrder,
  DirectEmailLandingOrder,
  SignupWizardOrder,
  SignUpWizardStepsRedesign,
  authWizardWrapperStyle,
} from './constants';
import styles from './RedesignWizard.module.scss';
import { SignUpWizardProvider } from './signUpWizard.provider';

const SignUpWizardComponents = {
  [SignUpWizardSteps.SignUpPrivacy]: SignUpPrivacy,
  [SignUpWizardSteps.SignUpEmailLanding]: SignUpEmailLanding,
  [SignUpWizardSteps.SignUpOptions]: SignUpOptions,
  [SignUpWizardSteps.ResponsibleSocialPlay]: ResponsibleSocialPlay,
  [SignUpWizardSteps.SignUpRegister]: SignUpRegister,
  [SignUpWizardSteps.EmailVerification]: EmailVerification,
  [SignUpWizardSteps.VerificationSuccess]: VerificationSuccess,
  [SignUpWizardSteps.WelcomeBonusDialog]: WelcomeBonusDialog,
  [SignUpWizardSteps.ChooseCountry]: ChooseCountry,
};

const SignUpWizardComponentsRedesign = {
  [SignUpWizardStepsRedesign.SignUpOptions]: SignUpOptionsRedesign,
  [SignUpWizardStepsRedesign.EmailVerification]: EmailVerificationRedesign,
  [SignUpWizardStepsRedesign.ChooseCountry]: ChooseCountryWizardStepRedesign,
  [SignUpWizardStepsRedesign.ResponsibleSocialPlay]: ResponsibleSocialPlayTermsRedesign,
} as const;

const getSteps = (
  authorized: boolean,
  isDirectSignUpLanding: boolean,
  isDirectEmailLanding: boolean,
  redesignFlow: boolean,
) => {
  if (!authorized && isDirectSignUpLanding) {
    return {
      order: DirectSignUpLandingOrder,
      sortedComponents: DirectSignUpLandingOrder.map((value) => SignUpWizardComponents[value]),
      isRedesignSteps: false,
    };
  }

  if (!authorized && isDirectEmailLanding) {
    return {
      order: DirectEmailLandingOrder,
      sortedComponents: DirectEmailLandingOrder.map((value) => SignUpWizardComponents[value]),
      isRedesignSteps: false,
    };
  }

  if (redesignFlow) {
    return {
      order: SignupWizardOrderRedesignFlow,
      sortedComponents: SignupWizardOrderRedesignFlow.map((value) => SignUpWizardComponentsRedesign[value]),
      isRedesignSteps: true,
    };
  }

  return {
    order: SignupWizardOrder,
    sortedComponents: SignupWizardOrder.map((value) => SignUpWizardComponents[value]),
    isRedesignSteps: false,
  };
};

export default function SignUpWizard() {
  const { authStore, riveFileStore } = useStores();
  const redesignFlow = useFlag<boolean>('ui_redesign_registration_flow', true);

  const { sortedComponents, isRedesignSteps } = getSteps(
    authStore.authorized,
    authStore.isDirectSignUpLanding(),
    authStore.isDirectEmailLanding(),
    redesignFlow,
  );

  useEffect(() => {
    if (!isRedesignSteps) return;
    riveFileStore.addFileToPreload(signUpEmailRiv);
  }, [isRedesignSteps, riveFileStore]);

  return (
    <SignUpWizardProvider>
      <Wizard
        classNameSteps={classNames({
          [styles.redesignFlowSteps]: isRedesignSteps,
        })}
        classNameStepWrapper={classNames({
          [styles.redesignFlowStepWrapper]: isRedesignSteps,
        })}
        steps={sortedComponents}
        wizardFlowParams={{ isRedesignSteps }}
        wrapperStyle={authWizardWrapperStyle}
      />
    </SignUpWizardProvider>
  );
}
